import { useState, useMemo, useEffect, useCallback } from "react";
import {
  Search, SlidersHorizontal, Plus, Pencil, Trash2, ShoppingCart,
  X, Check, AlertTriangle, LayoutList, Table2, ChevronDown, Tag,
  Loader2, CreditCard, Wallet, Banknote, Landmark, QrCode,
  Smartphone, Globe, Gift, DollarSign, RefreshCw,
} from "lucide-react";
import { usePaymentTypes } from "../context/PaymentTypeContext";

// ─── Platform config (matches by keyword in name, case-insensitive) ──────────

const PLATFORMS = {
  amazon: {
    label: "Amazon Orders", 
    badge: "bg-orange-100 text-orange-600 border border-orange-200",
    iconBg: "bg-orange-500", accent: "#f97316", emoji: "🟠",
  },
  flipkart: {
    label: "Flipkart Orders",
    badge: "bg-blue-100 text-blue-600 border border-blue-200",
    iconBg: "bg-blue-500", accent: "#3b82f6", emoji: "🔵",
  },
  meesho: {
    label: "Meesho Orders",
    badge: "bg-pink-100 text-pink-600 border border-pink-200",
    iconBg: "bg-pink-500", accent: "#ec4899", emoji: "🩷",
  },
  website: {
    label: "Website Orders", 
    badge: "bg-emerald-100 text-emerald-700 border border-emerald-200",
    iconBg: "bg-emerald-500", accent: "#10b981", emoji: "🌐",
  },
  instagram: {
    label: "Instagram Orders", 
    badge: "bg-purple-100 text-purple-600 border border-purple-200",
    iconBg: "bg-purple-500", accent: "#a855f7", emoji: "📸",
  },
};

// Fallback palette for unknown names
const FALLBACK_PALETTE = [
  { badge: "bg-yellow-100 text-yellow-700 border border-yellow-200", accent: "#eab308", iconBg: "bg-yellow-500", emoji: "🛒" },
  { badge: "bg-red-100 text-red-600 border border-red-200",          accent: "#ef4444", iconBg: "bg-red-500",    emoji: "📦" },
  { badge: "bg-cyan-100 text-cyan-700 border border-cyan-200",       accent: "#06b6d4", iconBg: "bg-cyan-500",   emoji: "🏪" },
  { badge: "bg-indigo-100 text-indigo-600 border border-indigo-200", accent: "#6366f1", iconBg: "bg-indigo-500", emoji: "🛍️" },
];

/**
 * Resolve styling for a payment type by name.
 * Tries to match any PLATFORMS key as a keyword (e.g. "amazon" inside "Amazon Orders").
 * Falls back to a deterministic color from the FALLBACK_PALETTE.
 */
function getStyle(name = "") {
  const lower = name.toLowerCase();
  const matchedKey = Object.keys(PLATFORMS).find((key) => lower.includes(key));
  if (matchedKey) return PLATFORMS[matchedKey];

  // Deterministic fallback
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return FALLBACK_PALETTE[Math.abs(hash) % FALLBACK_PALETTE.length];
}

// Short code: uses PLATFORMS shortLabel if matched, else derives from words
function getShortLabel(name = "") {
  const lower = name.toLowerCase();
  const matchedKey = Object.keys(PLATFORMS).find((key) => lower.includes(key));
  if (matchedKey) return PLATFORMS[matchedKey].shortLabel;
  const words = name.trim().split(/\s+/);
  return words.length === 1
    ? name.slice(0, 3).toUpperCase()
    : words.map((w) => w[0]).join("").toUpperCase().slice(0, 4);
}

// ─── Shared sub-components ───────────────────────────────────────────────────

function Toggle({ active, onToggle, disabled }) {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-teal-400 disabled:opacity-50 disabled:cursor-not-allowed ${active ? "bg-teal-600" : "bg-gray-200"}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-300 ${active ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

function PlatformIcon({ name, size = 36 }) {
  const { emoji, iconBg } = getStyle(name);
  return (
    <div
      className={`${iconBg} rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm`}
      style={{ width: size, height: size }}
    >
      <span style={{ fontSize: size * 0.45 }}>{emoji}</span>
    </div>
  );
}

// ─── List Card ───────────────────────────────────────────────────────────────

function ListCard({ item, onToggle, onEdit, onDelete, toggling }) {
  const { accent, badge } = getStyle(item.type);
  const isActive = item.status === "Active";

  return (
    <div
      className="bg-white border border-[#e8e0d0] rounded-2xl px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:shadow-md hover:border-teal-200 transition-all duration-200"
      style={{ borderLeft: `4px solid ${accent}` }}
    >
      <div className="flex items-center gap-4">
        <PlatformIcon name={item.type} size={42} />
        <div>
          <p className="font-semibold text-[#1a2e2b] text-[15px] tracking-tight">{item.type}</p>
        
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${isActive ? "bg-teal-50 text-teal-700 border border-teal-200" : "bg-gray-100 text-gray-500 border border-gray-200"}`}>
              {isActive ? "● Active" : "○ Inactive"}
            </span>
            {/* <span className={`text-[11px] font-bold px-2 py-0.5 rounded-md ${badge}`}>
              {getShortLabel(item.type)}
            </span> */}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Toggle
          active={isActive}
          onToggle={() => onToggle(item.id, item.status)}
          disabled={toggling === item.id}
        />
        {toggling === item.id && <Loader2 size={14} className="animate-spin text-teal-500" />}
    
        <button
          onClick={() => onDelete(item)}
          className="p-2 rounded-xl text-red-400 hover:text-red-500 hover:bg-red-50 transition-all"
        >
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}

// ─── Table View ──────────────────────────────────────────────────────────────

function TableView({ items, onToggle, onEdit, onDelete, toggling }) {
  return (
    <div className="bg-white border border-[#e8e0d0] rounded-2xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px]">
          <thead>
            <tr className="bg-[#faf6ee] border-b border-[#e8e0d0]">
              {["Payment Type", "Status", "Actions"].map((h, i) => (
                <th
                  key={h}
                  className={`px-5 py-3.5 text-[11px] font-bold uppercase tracking-widest text-[#8a7a5a] ${i === 2 ? "text-right" : "text-left"}`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#f0ebe0]">
            {items.map((item) => {
              const { accent, badge } = getStyle(item.type);
              const isActive = item.status === "Active";
              return (
                <tr key={item.id} className="hover:bg-[#faf8f3] transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="w-1 h-8 rounded-full flex-shrink-0" style={{ background: accent }} />
                      <PlatformIcon name={item.type} size={32} />
                      <span className="font-medium text-[#1a2e2b] text-sm">{item.type}</span>
                    </div>
                  </td>
                  {/* <td className="px-5 py-3.5">
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-md ${badge}`}>
                      {getShortLabel(item.type)}
                    </span>
                  </td> */}
                
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <Toggle
                        active={isActive}
                        onToggle={() => onToggle(item.id, item.status)}
                        disabled={toggling === item.id}
                      />
                      {toggling === item.id
                        ? <Loader2 size={13} className="animate-spin text-green-500" />
                        : <span className={`text-xs font-medium ${isActive ? "text-green-600" : "text-gray-400"}`}></span>
                      }
                    </div>
                  </td>
                 <td className="px-5 py-3.5 text-right">
  <button
    onClick={() => onDelete(item)}
    className="p-2 rounded-lg text-red-400 hover:text-red-500 hover:bg-red-50 transition-all"
  >
    <Trash2 size={14} />
  </button>
</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}



// ─── Delete Modal ────────────────────────────────────────────────────────────

function DeleteModal({ isOpen, deleteItem, onClose, onConfirm, submitting }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (isOpen) setTimeout(() => setVisible(true), 10);
    else setVisible(false);
  }, [isOpen]);

  if (!isOpen || !deleteItem) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center px-4 transition-all duration-300 ${visible ? "bg-black/40 backdrop-blur-sm" : "bg-black/0"}`}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={`bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden transition-all duration-300 ${visible ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 translate-y-4"}`}>
        <div className="h-1.5 w-full bg-red-500" />
        <div className="p-6 text-center">
          <div className="w-16 h-16 bg-red-50 border-2 border-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Trash2 size={24} className="text-red-500" />
          </div>
          <h2 className="text-lg font-bold text-[#1a2e2b] mb-1">Delete Payment Type?</h2>
          <p className="text-sm text-gray-500 mb-4 leading-relaxed">
            You're about to delete <span className="font-semibold text-[#1a2e2b]">{deleteItem.type}</span>. This action cannot be undone.
          </p>
          <div className="flex items-center gap-3 bg-[#faf6ee] border border-[#e8e0d0] rounded-2xl p-3 mb-5">
            <PlatformIcon name={deleteItem.type} size={36} />
            <div className="text-left">
              <p className="font-semibold text-sm text-[#1a2e2b]">{deleteItem.type}</p>
              <span className={`text-[11px] font-bold px-2 py-0.5 rounded-md ${getStyle(deleteItem.type).badge}`}>
                {getShortLabel(deleteItem.type)}
              </span>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-2xl border border-[#e8e0d0] text-sm text-gray-500 hover:bg-gray-50 font-medium transition-all"
            >
              Cancel
            </button>
            <button
              onClick={() => onConfirm(deleteItem.id)}
              disabled={submitting}
              className="flex-1 py-3 rounded-2xl bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-sm font-bold shadow-lg shadow-red-200 transition-all active:scale-95"
            >
              <span className="flex items-center justify-center gap-2">
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Delete
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Toast ───────────────────────────────────────────────────────────────────

function Toast({ toast }) {
  if (!toast) return null;
  const isError = toast.type === "error";
  return (
    <div className={`fixed bottom-6 right-6 z-[60] flex items-center gap-3 px-4 py-3 rounded-2xl shadow-xl text-sm font-semibold transition-all duration-300 ${isError ? "bg-red-500 text-white" : "bg-teal-600 text-white"}`}>
      {isError ? <AlertTriangle size={15} /> : <Check size={15} />}
      {toast.message}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function OrderTypesPage() {
  const {
    paymentTypes,
    paymentTypesLoading,
    fetchPaymentTypes,
    addPaymentType,
    updatePaymentType,
    deletePaymentType,
    toggleStatus,
  } = usePaymentTypes();

  // UI state
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name-asc");

  // Modal state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  // Operation loading states
  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState(null); // item id being toggled

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // ── Derived counts ──────────────────────────────────────────────────────
  const activeCount = paymentTypes.filter((p) => p.status === "Active").length;
  const inactiveCount = paymentTypes.length - activeCount;

  // ── Filtered + sorted list ──────────────────────────────────────────────
  const filtered = useMemo(() => {
    let result = [...paymentTypes];
    if (search) result = result.filter((p) => p.name?.toLowerCase().includes(search.toLowerCase()));
    if (statusFilter === "active") result = result.filter((p) => p.status === "Active");
    if (statusFilter === "inactive") result = result.filter((p) => p.status !== "Active");
    result.sort((a, b) => {
      if (sortBy === "name-asc") return (a.name || "").localeCompare(b.name || "");
      if (sortBy === "name-desc") return (b.name || "").localeCompare(a.name || "");
      if (sortBy === "status-active") return (b.status === "Active") - (a.status === "Active");
      if (sortBy === "status-inactive") return (a.status === "Active") - (b.status === "Active");
      return 0;
    });
    return result;
  }, [paymentTypes, search, statusFilter, sortBy]);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleToggle = async (id, currentStatus) => {
    setToggling(id);
    try {
      await toggleStatus(id, currentStatus);
      showToast(`Status updated successfully`);
    } catch {
      showToast("Failed to update status", "error");
    } finally {
      setToggling(null);
    }
  };

  const handleOpenAdd = () => { setEditItem(null); setFormModalOpen(true); };
  const handleOpenEdit = (item) => { setEditItem(item); setFormModalOpen(true); };

  const handleFormSubmit = async (data) => {
    setSubmitting(true);
    try {
      if (editItem) {
        await updatePaymentType(editItem.id, data);
        showToast("Payment type updated");
      } else {
        await addPaymentType(data);
        showToast("Payment type added");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePrompt = (item) => { setDeleteItem(item); setDeleteModalOpen(true); };

  const handleDeleteConfirm = async (id) => {
    setSubmitting(true);
    try {
      await deletePaymentType(id);
      showToast("Payment type deleted");
      setDeleteModalOpen(false);
      setDeleteItem(null);
    } catch {
      showToast("Failed to delete payment type", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#fff8ef] font-sans">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-teal-600 flex items-center justify-center shadow-lg shadow-teal-200">
              <CreditCard size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1a2e2b] tracking-tight">Order Types</h1>
              <p className="text-sm text-[#8a7a5a] mt-0.5">Manage Order Types</p>
            </div>
          </div>
        
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          {[
            { label: "Total Types",  value: paymentTypes.length, color: "text-[#1a2e2b]", bg: "bg-white" },
            { label: "Active",       value: activeCount,          color: "text-teal-600",  bg: "bg-teal-50 border-teal-100" },
            { label: "Inactive",     value: inactiveCount,        color: "text-gray-500",  bg: "bg-gray-50 border-gray-100" },
          ].map((s) => (
            <div key={s.label} className={`${s.bg} border border-[#e8e0d0] rounded-2xl px-4 py-3 text-center`}>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-[11px] text-[#8a7a5a] font-medium mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Search + Filters */}
        <div className="bg-white border border-[#e8e0d0] rounded-2xl shadow-sm p-3 mb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search Order types..."
                className="w-full pl-10 pr-4 py-2.5 bg-[#faf8f3] border border-[#e8e0d0] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:bg-white transition-all text-[#1a2e2b] placeholder-gray-400"
              />
            </div>
            <button
              onClick={() => setFiltersOpen(!filtersOpen)}
              className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${filtersOpen ? "border-teal-500 bg-teal-600 text-white" : "border-[#e8e0d0] bg-[#faf8f3] hover:bg-gray-100 text-[#1a2e2b]"}`}
            >
              <SlidersHorizontal size={14} />
              Filters
              <ChevronDown size={12} className={`transition-transform duration-200 ${filtersOpen ? "rotate-180" : ""}`} />
            </button>
          </div>

          {filtersOpen && (
            <div className="mt-4 pt-4 border-t border-[#e8e0d0] grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#1a2e2b] mb-2">Status</label>
                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full appearance-none bg-white border border-[#e8e0d0] rounded-xl px-4 py-2.5 text-sm text-[#1a2e2b] focus:outline-none focus:ring-2 focus:ring-teal-300 cursor-pointer pr-9"
                  >
                    <option value="all">All Types ({paymentTypes.length})</option>
                    <option value="active">Active ({activeCount})</option>
                    <option value="inactive">Inactive ({inactiveCount})</option>
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1a2e2b] mb-2">Sort By</label>
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full appearance-none bg-white border border-[#e8e0d0] rounded-xl px-4 py-2.5 text-sm text-[#1a2e2b] focus:outline-none focus:ring-2 focus:ring-teal-300 cursor-pointer pr-9"
                  >
                    <option value="name-asc">Name A–Z</option>
                    <option value="name-desc">Name Z–A</option>
                   
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Count + View toggle */}
        <div className="flex items-center justify-between mb-3 px-1">
          <p className="text-sm text-[#8a7a5a]">
            Showing <span className="font-bold text-[#1a2e2b]">{filtered.length}</span> of{" "}
            <span className="font-bold text-[#1a2e2b]">{paymentTypes.length}</span> Order Types
          </p>
          <div className="flex items-center bg-white border border-[#e8e0d0] rounded-xl overflow-hidden shadow-sm">
            {[
              { mode: "list",  Icon: LayoutList, title: "List view" },
              { mode: "table", Icon: Table2,     title: "Table view" },
            ].map(({ mode, Icon, title }) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`p-2.5 transition-all ${viewMode === mode ? "bg-teal-600 text-white" : "text-gray-400 hover:text-gray-600 hover:bg-gray-50"}`}
                title={title}
              >
                <Icon size={15} />
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {paymentTypesLoading && paymentTypes.length === 0 ? (
          <div className="bg-white rounded-2xl border border-[#e8e0d0] py-16 flex flex-col items-center justify-center text-gray-400">
            <Loader2 size={32} className="mb-3 animate-spin text-teal-400" />
            <p className="text-sm font-medium">Loading order types…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-[#e8e0d0] py-16 flex flex-col items-center justify-center text-gray-400">
            <CreditCard size={32} className="mb-3 opacity-30" />
            <p className="text-sm font-medium">No Order types found</p>
            <p className="text-xs mt-1 opacity-60">
              {paymentTypes.length === 0 ? "Add your first payment type to get started" : "Try adjusting your search or filters"}
            </p>
            {paymentTypes.length === 0 && (
              <button
                onClick={handleOpenAdd}
                className="mt-4 flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700 transition-all"
              >
                <Plus size={14} /> Add Payment Type
              </button>
            )}
          </div>
        ) : viewMode === "list" ? (
          <div className="space-y-3">
            {filtered.map((item) => (
              <ListCard
                key={item.id}
                item={item}
                onToggle={handleToggle}
                onEdit={handleOpenEdit}
                onDelete={handleDeletePrompt}
                toggling={toggling}
              />
            ))}
          </div>
        ) : (
          <TableView
            items={filtered}
            onToggle={handleToggle}
            onEdit={handleOpenEdit}
            onDelete={handleDeletePrompt}
            toggling={toggling}
          />
        )}
      </main>

    
      <DeleteModal
        isOpen={deleteModalOpen}
        deleteItem={deleteItem}
        onClose={() => { setDeleteModalOpen(false); setDeleteItem(null); }}
        onConfirm={handleDeleteConfirm}
        submitting={submitting}
      />
      <Toast toast={toast} />
    </div>
  );
}